import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { Home, Utensils, Bus, ShoppingBag, ShieldCheck, Zap, Plane, Stamp, Package, Save } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { countries, cities, oneTimeCosts } from "@/data/costs";
import { computeCost, computeFundingGap } from "@/lib/calc";
import { Button } from "@/components/ui/button";
import { SectionTitle, Disclaimer, EmptyState } from "@/components/common";

const itemMeta = {
  housing: { label: "Housing", icon: Home }, food: { label: "Food", icon: Utensils },
  transport: { label: "Transportation", icon: Bus }, personal: { label: "Personal", icon: ShoppingBag },
  utilities: { label: "Utilities", icon: Zap }, insurance: { label: "Insurance", icon: ShieldCheck },
};

export default function CostCalculator() {
  const { data, updateData } = useApp();
  const [countryCode, setCountryCode] = useState("GB");
  const [cityName, setCityName] = useState("London");
  const [months, setMonths] = useState(12);
  const [lifestyle, setLifestyle] = useState("Standard");
  const [confirmedFunding, setConfirmedFunding] = useState(0);

  const country = countries.find((c) => c.code === countryCode);
  const cityList = cities[countryCode] || [];
  const city = cityList.find((c) => c.name === cityName) || cityList[0];
  const oneTime = oneTimeCosts[countryCode];

  const result = useMemo(() => (city ? computeCost({ city, months, lifestyle, oneTime }) : null), [city, months, lifestyle, oneTime]);
  const fmt = (n) => `${country.symbol}${n.toLocaleString("en-US")}`;
  const idr = (n) => `Rp ${Math.round((n * country.idrRate) / 1e6).toLocaleString("id-ID")} jt`;
  const gap = result ? computeFundingGap(result.total, confirmedFunding) : 0;

  const onCountry = (code) => { setCountryCode(code); setCityName((cities[code] || [])[0]?.name || ""); };

  const save = () => {
    updateData({ costPrefs: { country: country.name, city: cityName, months, lifestyle, summary: `${fmt(result.monthly)}/bln`, total: result.total } });
    toast.success("Estimasi biaya tersimpan ke dashboard.");
  };

  return (
    <div className="space-y-6">
      <SectionTitle title="Kalkulator Biaya Hidup" desc="Berapa yang perlu kamu siapkan? Semua perhitungan dilakukan lokal di browsermu." />
      <div className="grid gap-6 lg:grid-cols-12">
        {/* Inputs */}
        <div className="space-y-4 rounded-2xl border border-purple-100 bg-white p-5 shadow-sm lg:col-span-4">
          <Field label="Negara"><select data-testid="cost-country" value={countryCode} onChange={(e) => onCountry(e.target.value)} className={selectCls}>{countries.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}</select></Field>
          <Field label="Kota"><select data-testid="cost-city" value={cityName} onChange={(e) => setCityName(e.target.value)} className={selectCls}>{cityList.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}</select></Field>
          <Field label="Durasi (bulan)"><select data-testid="cost-months" value={months} onChange={(e) => setMonths(+e.target.value)} className={selectCls}>{[6, 12, 18, 24].map((m) => <option key={m} value={m}>{m} bulan</option>)}</select></Field>
          <Field label="Gaya Hidup"><select data-testid="cost-lifestyle" value={lifestyle} onChange={(e) => setLifestyle(e.target.value)} className={selectCls}>{["Budget", "Standard", "Higher Comfort"].map((l) => <option key={l} value={l}>{l}</option>)}</select></Field>
          <Field label="Beasiswa Terkonfirmasi (offset, opsional)">
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-400">{country.symbol}</span>
              <input type="number" min="0" data-testid="cost-funding" value={confirmedFunding} onChange={(e) => setConfirmedFunding(+e.target.value || 0)} className={selectCls} />
            </div>
          </Field>
          <Button onClick={save} data-testid="cost-save" className="w-full rounded-full bg-[#7C3AED] hover:bg-[#6D28D9]"><Save className="mr-1.5 h-4 w-4" /> Simpan ke Dashboard</Button>
        </div>

        {/* Output */}
        <div className="space-y-4 lg:col-span-8">
          {!city ? (
            <EmptyState testid="cost-empty" title="Belum ada data biaya untuk kota ini" desc="Pilih negara & kota lain untuk melihat estimasi." />
          ) : (
            <>
              <div className="rounded-2xl border border-purple-100 bg-white p-5 shadow-sm">
                <div className="flex flex-col justify-between gap-2 sm:flex-row sm:items-end">
                  <div>
                    <p className="text-sm text-slate-500">{cityName}, {country.name} · biaya hidup / bulan</p>
                    <div className="mt-1 flex items-end gap-2">
                      <span className="font-display text-4xl font-extrabold text-[#5B21B6]" data-testid="cost-monthly">{fmt(result.monthly)}</span>
                      <span className="mb-1 text-sm text-slate-400">≈ {idr(result.monthly)}</span>
                    </div>
                  </div>
                </div>
                <div className="mt-4 grid gap-2 sm:grid-cols-2">
                  {Object.entries(result.monthlyItems).map(([k, v]) => {
                    const M = itemMeta[k]; const Icon = M.icon;
                    return (
                      <div key={k} className="flex items-center justify-between rounded-xl border border-purple-50 bg-[#FBFAFF] px-3 py-2 text-sm">
                        <span className="flex items-center gap-2 text-slate-600"><Icon className="h-4 w-4 text-[#A78BFA]" /> {M.label}</span>
                        <span className="font-semibold text-slate-800">{fmt(v)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-3">
                <Metric label="Estimasi Bulanan" value={fmt(result.monthly)} />
                <Metric label="Estimasi Tahunan" value={fmt(result.annualLiving)} />
                <Metric label={`Total Program (${months} bln)`} value={fmt(result.total)} highlight />
              </div>

              {/* Gross - funding = remaining */}
              <div className="rounded-2xl border border-purple-100 bg-white p-5 shadow-sm">
                <h3 className="font-display text-base font-bold text-slate-900">Estimasi Kebutuhan Dana</h3>
                <div className="mt-3 space-y-2 text-sm">
                  <Row label={`Gross Cost (living + tuition + setup, ${months} bln)`} value={fmt(result.total)} />
                  <Row label="Termasuk tuition" value={fmt(result.tuitionTotal)} muted />
                  <Row label="Biaya awal (visa + tiket + setup)" value={fmt(result.oneTimeTotal)} muted />
                  <Row label="− Beasiswa terkonfirmasi" value={`− ${fmt(confirmedFunding)}`} muted />
                  <div className="border-t border-purple-100 pt-2"><Row label="Sisa estimasi biaya" value={fmt(gap)} bold /></div>
                </div>
              </div>

              <div className="rounded-2xl border border-purple-100 bg-[#FBFAFF] p-4 text-xs text-slate-500">
                <b className="text-[#6D28D9]">Asumsi:</b> Kota {cityName} · Gaya hidup {lifestyle} · Durasi {months} bulan · Kurs 1 {country.currency} ≈ Rp {country.idrRate.toLocaleString("id-ID")} · Tahun estimasi 2025 · Housing = sewa rata-rata. Angka bersifat estimasi awal.
              </div>
              <Disclaimer>Pendanaan hipotetis tidak diperlakukan sebagai pendanaan terkonfirmasi. Masukkan hanya benefit yang sudah pasti kamu terima.</Disclaimer>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const selectCls = "w-full cursor-pointer rounded-xl border border-purple-100 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none focus:border-[#7C3AED]";
const Field = ({ label, children }) => <div><label className="mb-1.5 block text-sm font-medium text-slate-600">{label}</label>{children}</div>;
const Metric = ({ label, value, highlight }) => (
  <div className={`rounded-2xl border p-4 shadow-sm ${highlight ? "border-transparent bg-gradient-to-br from-[#5B21B6] to-[#8B5CF6] text-white" : "border-purple-100 bg-white"}`}>
    <p className={`text-xs ${highlight ? "text-purple-100" : "text-slate-400"}`}>{label}</p>
    <p className={`mt-1 font-display text-xl font-extrabold ${highlight ? "text-white" : "text-slate-900"}`}>{value}</p>
  </div>
);
const Row = ({ label, value, muted, bold }) => (
  <div className="flex items-center justify-between">
    <span className={muted ? "text-slate-400" : bold ? "font-bold text-slate-900" : "text-slate-600"}>{label}</span>
    <span className={bold ? "font-display text-lg font-extrabold text-[#5B21B6]" : muted ? "text-slate-400" : "font-semibold text-slate-800"}>{value}</span>
  </div>
);
